import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, ArrowRight, Check, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
import { CmsButton, CmsImage, SmartLink, SectionShell, SectionHeader, RichText, isDarkBg, SHADCN_BTN } from '../shared';
import { currentSource } from '../../../lib/consultSource';
import { ContactDetails } from './ContactInfoSection';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const CtaSection = ({ section }) => {
  const p = section.props || {};
  const layout = section.layout || 'dark';
  const forcedBg = layout === 'dark' ? 'dark' : layout === 'light' ? 'light' : (section.style?.background || 'light');
  const sec = { ...section, style: { ...(section.style || {}), background: forcedBg || 'dark', padding: layout === 'banner' ? 'compact' : section.style?.padding } };
  const dark = isDarkBg(sec.style);

  if (layout === 'split_image') {
    return (
      <SectionShell section={{ ...sec, style: { ...sec.style, padding: 'none' } }} container="none">
        <div className="grid lg:grid-cols-2">
          <div className="container-xl lg:max-w-none lg:px-16 py-20 lg:py-28 flex flex-col justify-center">
            {p.eyebrow && <span className={`label-overline ${dark ? 'text-[color:var(--site-accent-light)]' : 'text-[color:var(--site-accent)]'}`}>{p.eyebrow}</span>}
            <h2 className={`mt-4 ${dark ? 'text-white' : 'text-neutral-900'}`}>{p.title}</h2>
            <RichText html={p.body} className={`mt-5 text-lg ${dark ? 'text-neutral-300' : 'text-neutral-600'}`} />
            <div className="mt-8 flex flex-wrap gap-4"><CmsButton button={p.primary_button} variant={dark ? 'light' : 'primary'} testId="cta-primary-btn" /><CmsButton button={p.secondary_button} variant={dark ? 'outline_light' : 'secondary'} /></div>
          </div>
          <CmsImage image={p.image} className="w-full h-full min-h-[320px] object-cover rounded-none" />
        </div>
      </SectionShell>
    );
  }

  if (layout === 'checklist') {
    const bullets = (p.bullets || []).filter((b) => b.text);
    return (
      <SectionShell section={{ ...sec, style: { background: 'light', padding: 'compact', ...(section.style || {}) } }}>
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div>
            {p.eyebrow && <span className={`text-xs font-medium tracking-wider uppercase ${dark ? 'text-[color:var(--site-accent-light)]' : 'text-[color:var(--site-accent)]'}`}>{p.eyebrow}</span>}
            <h2 className={`mt-2 !text-3xl !font-semibold ${dark ? 'text-white' : 'text-neutral-900'}`}>{p.title}</h2>
            <RichText html={p.body} className={`mt-3 leading-relaxed ${dark ? 'text-neutral-300' : 'text-neutral-600'}`} />
            <div className="mt-5 flex flex-wrap gap-3">
              {p.primary_button?.text && <SmartLink to={p.primary_button.link || '#'} className="inline-flex items-center justify-center gap-2 h-9 px-5 py-2 text-sm font-medium rounded-md shadow bg-neutral-900 text-white hover:bg-neutral-800 transition-colors" data-testid="cta-primary-btn"><Calendar className="w-4 h-4 mr-2" />{p.primary_button.text}</SmartLink>}
              {p.secondary_button?.text && <SmartLink to={p.secondary_button.link || '#'} className="inline-flex items-center justify-center h-9 px-5 py-2 text-sm font-medium rounded-md border border-input bg-white text-neutral-900 shadow-sm hover:bg-neutral-100 transition-colors" data-testid="cta-secondary-btn">{p.secondary_button.text}</SmartLink>}
            </div>
          </div>
          {(p.checklist_title || bullets.length > 0) && (
            <div className="bg-white p-6 rounded-lg shadow-md text-neutral-900" data-testid="cta-checklist">
              {p.checklist_title && <h3 className="!text-lg !font-medium mb-4">{p.checklist_title}</h3>}
              <ul className="space-y-2">{bullets.map((b, i) => <li key={i} className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-green-500 flex-shrink-0" /><span className="text-neutral-700">{b.text}</span></li>)}</ul>
            </div>
          )}
        </div>
      </SectionShell>
    );
  }

  if (layout === 'banner') {
    return (
      <SectionShell section={sec}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div><h3 className={`text-2xl font-semibold ${dark ? 'text-white' : 'text-neutral-900'}`}>{p.title}</h3>{p.body && <p className={`mt-1 ${dark ? 'text-neutral-300' : 'text-neutral-600'}`}>{p.body}</p>}</div>
          <div className="flex gap-3 flex-shrink-0"><CmsButton button={p.primary_button} variant={dark ? 'light' : 'primary'} testId="cta-primary-btn" /><CmsButton button={p.secondary_button} variant={dark ? 'outline_light' : 'secondary'} /></div>
        </div>
      </SectionShell>
    );
  }

  if (layout === 'dark') {
    // Mirrors the coded Home/About CTA: shadcn-sized buttons (white · amber+calendar · outline-light).
    return (
      <SectionShell section={sec}>
        <div className="text-center">
          {p.eyebrow && <span className="label-overline text-[color:var(--site-accent-light)]">{p.eyebrow}</span>}
          <h2 className={`${p.eyebrow ? 'mt-4' : ''} text-white mb-6`}>{p.title}</h2>
          <RichText html={p.body} className="text-neutral-400 max-w-2xl mx-auto mb-10" />
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <CmsButton button={p.primary_button} className={`${SHADCN_BTN} !bg-white !text-neutral-900 hover:!bg-neutral-100`} icon={false} testId="cta-primary-btn" />
            {p.secondary_button?.text && <SmartLink to={p.secondary_button.link || '#'} className={`${SHADCN_BTN} gap-2 text-sm font-medium tracking-wide uppercase !bg-amber-600 !text-white hover:!bg-amber-700`} data-testid="cta-secondary-btn"><Calendar className="w-4 h-4 mr-2" />{p.secondary_button.text}</SmartLink>}
            <CmsButton button={p.tertiary_button} className={`${SHADCN_BTN} !bg-transparent !text-white border border-white/30 hover:!bg-white hover:!text-neutral-900`} icon={false} testId="cta-tertiary-btn" />
          </div>
        </div>
      </SectionShell>
    );
  }

  if (layout === 'light') {
    // Mirrors the coded FAQ CTA: classic .btn-primary / .btn-secondary links.
    return (
      <SectionShell section={sec}>
        <div className="text-center">
          {p.eyebrow && <span className="label-overline text-[color:var(--site-accent)]">{p.eyebrow}</span>}
          <h2 className={`${p.eyebrow ? 'mt-4' : ''} mb-6 text-neutral-900`}>{p.title}</h2>
          <RichText html={p.body} className="text-neutral-600 max-w-2xl mx-auto mb-8" />
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <CmsButton button={p.primary_button} className="btn-primary !px-8 justify-center !rounded-none" icon={false} testId="cta-primary-btn" />
            <CmsButton button={p.secondary_button} className="btn-secondary !px-8 justify-center !rounded-none !bg-transparent !text-neutral-900 hover:!bg-neutral-900 hover:!text-white" icon={false} testId="cta-secondary-btn" />
          </div>
        </div>
      </SectionShell>
    );
  }

  if (layout === 'centered') {
    // Mirrors the coded Partners CTA: narrow centered copy, large buttons.
    return (
      <SectionShell section={sec}>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-3xl mx-auto text-center">
          {p.eyebrow && <span className={`label-overline ${dark ? 'text-[color:var(--site-accent-light)]' : 'text-[color:var(--site-accent)]'}`}>{p.eyebrow}</span>}
          <h2 className={`!text-3xl md:!text-4xl !leading-tight mb-6 ${dark ? 'text-white' : 'text-neutral-900'}`}>{p.title}</h2>
          <RichText html={p.body} className={`text-lg mb-8 ${dark ? 'text-neutral-300' : 'text-neutral-600'}`} />
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <CmsButton button={p.primary_button} className="btn-primary !px-8 !py-4 !text-lg justify-center !rounded-none" icon={false} testId="cta-primary-btn" />
            <CmsButton button={p.secondary_button} className="btn-outline !px-8 !py-4 !text-lg justify-center !rounded-none !bg-transparent !text-neutral-900 hover:!bg-neutral-900 hover:!text-white" icon={false} testId="cta-secondary-btn" />
          </div>
        </motion.div>
      </SectionShell>
    );
  }

  return (
    <SectionShell section={sec} container="lg">
      <div className="text-center max-w-4xl mx-auto">
        {p.eyebrow && <span className={`label-overline ${dark ? 'text-[color:var(--site-accent-light)]' : 'text-[color:var(--site-accent)]'}`}>{p.eyebrow}</span>}
        <h2 className={`mt-5 ${dark ? 'text-white' : 'text-neutral-900'}`}>{p.title}</h2>
        <RichText html={p.body} className={`mt-5 text-lg ${dark ? 'text-neutral-300' : 'text-neutral-600'} max-w-2xl mx-auto`} />
        <div className="mt-10 flex flex-wrap justify-center gap-4"><CmsButton button={p.primary_button} variant={dark ? 'light' : 'primary'} testId="cta-primary-btn" /><CmsButton button={p.secondary_button} variant={dark ? 'accent' : 'secondary'} /><CmsButton button={p.tertiary_button} variant={dark ? 'outline_light' : 'secondary'} /></div>
      </div>
    </SectionShell>
  );
};

const FORM_META = {
  contact: { endpoint: '/contact', fields: ['name', 'email', 'phone', 'company', 'project_type', 'subject', 'message'], required: ['name', 'email', 'subject', 'message'], submit: 'Send Message', sending: 'Sending...', rows: 6, success_title: 'Message Received', success_box: true },
  trade_application: { endpoint: '/trade/apply', fields: ['name', 'company', 'email', 'phone', 'website', 'business_type', 'projects_per_year', 'how_found_us', 'message'], required: ['name', 'company', 'email', 'phone', 'business_type', 'how_found_us'], submit: 'Submit Application', sending: 'Submitting...', auth: true, full_submit: true, rows: 4, placeholders: { website: 'https://', message: 'Tell us about your business...' }, success_title: 'Application Received' },
  consultation: { endpoint: '/consultation/request', fields: ['name', 'email', 'phone', 'project_type', 'preferred_date', 'address', 'message'] },
  quote: { endpoint: '/contact', fields: ['name', 'email', 'phone', 'project_type', 'message'], subject: 'Quote Request (website form)' },
  fabric_quote: { redirect: '/fabrics', label: 'Browse fabrics & request a quote' },
  sample_request: { redirect: '/request-samples', label: 'Order free samples' },
};
const LABELS = { name: 'Full name', email: 'Email', phone: 'Phone', subject: 'Subject', message: 'Message', project_type: 'Project type', preferred_date: 'Preferred date', address: 'Project address', company: 'Company', website: 'Website', business_type: 'Business type', projects_per_year: 'Projects per year', how_found_us: 'How did you find us?' };
const SELECTS = {
  project_type: [['residential', 'Residential'], ['commercial', 'Commercial'], ['trade', 'Trade Inquiry'], ['other', 'Other']],
  business_type: [['interior-designer', 'Interior Designer'], ['architect', 'Architect'], ['builder-developer', 'Builder / Developer'], ['contractor', 'Contractor'], ['dealer-reseller', 'Dealer / Reseller'], ['custom-av-integrator', 'Custom AV Integrator']],
  projects_per_year: [['1-5', '1-5 projects'], ['6-15', '6-15 projects'], ['16-30', '16-30 projects'], ['30+', '30+ projects']],
  how_found_us: [['referral', 'Referral / Word of Mouth'], ['google', 'Google Search'], ['social-media', 'Social Media'], ['trade-show', 'Trade Show / Event'], ['industry-publication', 'Industry Publication'], ['other', 'Other']],
};

// Exact shadcn Input/Textarea/Button classes so builder forms match the coded pages.
const SHADCN_CTRL = 'flex w-full rounded-md border border-input bg-transparent text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:text-sm';

const Field = ({ k, value, onChange, required, labeled, meta = {} }) => {
  const base = labeled ? `${SHADCN_CTRL} h-9 px-3 py-1`
    : 'w-full border border-neutral-300 bg-white px-4 py-3 text-sm text-neutral-900 focus:outline-none focus:border-neutral-900 rounded-[var(--site-radius)]';
  const ph = labeled ? (meta.placeholders?.[k] || '') : LABELS[k];
  if (k === 'message') return <textarea rows={meta.rows || 4} className={labeled ? `${SHADCN_CTRL} min-h-[60px] px-3 py-2` : base} placeholder={ph} value={value} onChange={onChange} required={required} data-testid={`cms-form-${k}`} />;
  if (SELECTS[k]) return (
    <select className={labeled ? 'w-full input-form' : base} value={value} onChange={onChange} required={required} data-testid={`cms-form-${k}`}><option value="">{labeled ? 'Select...' : LABELS[k]}</option>{SELECTS[k].map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
  );
  return <input type={k === 'email' ? 'email' : k === 'phone' ? 'tel' : k === 'preferred_date' ? 'date' : 'text'} className={base} placeholder={ph} value={value} onChange={onChange} required={required} data-testid={`cms-form-${k}`} />;
};

const WIDE_FIELDS = ['website', 'business_type', 'message', 'address', 'subject', 'project_type'];
const LABELED_LABELS = { ...LABELS, company: 'Company Name', message: 'Additional Information' };
const CONTACT_LABELS = { ...LABELS, message: 'Message' };

const LabeledField = ({ k, value, onChange, required, meta, labels }) => (
  <div className={WIDE_FIELDS.includes(k) ? 'sm:col-span-2' : ''}>
    <label className="label-overline !text-sm !leading-none">{labels[k]}{required ? ' *' : ''}</label>
    {k === 'business_type' ? (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2" data-testid="cms-form-business_type">
        {SELECTS.business_type.map(([v, l]) => (
          <button key={v} type="button" onClick={() => onChange({ target: { value: v } })}
            className={`p-3 text-sm border transition-colors ${value === v ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-400'}`}>{l}</button>
        ))}
      </div>
    ) : (
      <div className="mt-2"><Field k={k} value={value} onChange={onChange} required={required} labeled meta={meta} /></div>
    )}
  </div>
);

export const FormSection = ({ section }) => {
  const p = section.props || {};
  const meta = FORM_META[p.form_type] || FORM_META.contact;
  const [values, setValues] = useState({});
  const [state, setState] = useState('idle');
  const successRef = useRef(null);
  useEffect(() => {
    const el = successRef.current;
    if (state !== 'done' || !el) return;
    const r = el.getBoundingClientRect();
    if (r.top < 80 || r.bottom > window.innerHeight) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [state]);
  const dark = isDarkBg(section.style);
  const layout = section.layout || 'standard';

  const submit = async (e) => {
    e.preventDefault();
    setState('sending');
    try {
      const payload = { ...values };
      if (meta.subject) payload.subject = meta.subject;
      if (p.form_type === 'consultation' && !payload.project_type) payload.project_type = 'residential';
      if (p.form_type === 'consultation') Object.assign(payload, currentSource(`Embedded form: ${p.title || 'Consultation'}`));
      if (p.form_type === 'quote') Object.assign(payload, currentSource(`Embedded form: ${p.title || 'Quote'}`), { quote_channel: 'cms_form' });
      const token = localStorage.getItem('apollo_token');
      await axios.post(`${API_URL}${meta.endpoint}`, payload, meta.auth && token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
      setState('done');
    } catch (err) {
      setState('idle');
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || 'Could not send. Please try again.');
    }
  };

  const form = meta.redirect ? (
    <SmartLink to={meta.redirect} className="inline-flex items-center gap-2 btn-primary rounded-[var(--site-btn-radius)]" data-testid="cms-form-redirect">{meta.label}<ArrowRight className="w-4 h-4" /></SmartLink>
  ) : state === 'done' ? (
    <div className="flex items-start gap-3 p-6 bg-green-50 border border-green-200 text-green-900 rounded-[var(--site-radius)]" data-testid="cms-form-success" ref={successRef}><CheckCircle2 className="w-6 h-6 flex-shrink-0" /><p>{p.success_message || 'Thank you — we received your request and will be in touch shortly.'}</p></div>
  ) : (
    <form onSubmit={submit} className={`space-y-4 ${layout === 'card' ? 'p-8 bg-white border border-neutral-200 rounded-[var(--site-radius)]' : ''}`} data-testid={`cms-form-${p.form_type || 'contact'}`}>
      <div className="grid sm:grid-cols-2 gap-4">
        {meta.fields.filter((k) => k !== 'message').map((k) => <Field key={k} k={k} value={values[k] || ''} required={(meta.required || ['name', 'email']).includes(k)} onChange={(e) => setValues({ ...values, [k]: e.target.value })} />)}
      </div>
      {meta.fields.includes('message') && <Field k="message" value={values.message || ''} required={p.form_type !== 'consultation'} onChange={(e) => setValues({ ...values, message: e.target.value })} />}
      <button type="submit" disabled={state === 'sending'} className="btn-primary rounded-[var(--site-btn-radius)] disabled:opacity-60" data-testid="cms-form-submit">{state === 'sending' ? 'Sending…' : meta.submit || 'Send Request'}</button>
    </form>
  );

  // Labeled variant: replicates the coded Trade / Contact forms (label above each control, rows of two).
  const required = meta.required || ['name', 'email'];
  const labels = p.form_type === 'trade_application' ? LABELED_LABELS : CONTACT_LABELS;
  const groups = meta.fields.reduce((acc, k) => {
    const last = acc[acc.length - 1];
    if (!WIDE_FIELDS.includes(k) && last && last.length === 1 && !WIDE_FIELDS.includes(last[0])) last.push(k); else acc.push([k]);
    return acc;
  }, []);
  const labeledForm = meta.redirect ? form : state === 'done' ? (
    <div className={meta.success_box ? 'bg-neutral-50 p-12 text-center' : 'text-center py-12'} data-testid="cms-form-success" ref={successRef}>
      <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6"><Check className="w-8 h-8 text-amber-600" /></div>
      <h3 className="!text-xl mb-2">{meta.success_title || 'Request Received'}</h3>
      <p className="text-neutral-600">{p.success_message || 'Thank you — we received your request and will be in touch shortly.'}</p>
    </div>
  ) : (
    <form onSubmit={submit} className="space-y-6" data-testid={`cms-form-${p.form_type || 'contact'}`}>
      {groups.map((g) => (
        <div key={g[0]} className="grid sm:grid-cols-2 gap-4">
          {g.map((k) => <LabeledField key={k} k={k} value={values[k] || ''} required={required.includes(k)} meta={meta} labels={labels} onChange={(e) => setValues({ ...values, [k]: e.target.value })} />)}
        </div>
      ))}
      <button type="submit" disabled={state === 'sending'} className={`btn-primary ${SHADCN_BTN} disabled:opacity-50 ${meta.full_submit ? 'w-full' : ''}`} data-testid="cms-form-submit">{state === 'sending' ? meta.sending || 'Sending...' : meta.submit || 'Send Request'}</button>
    </form>
  );

  if (layout === 'split_wide') {
    return (
      <SectionShell section={section}>
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          <div className="lg:col-span-2">
            {p.eyebrow && <span className={`label-overline ${dark ? 'text-[color:var(--site-accent-light)]' : 'text-[color:var(--site-accent)]'}`}>{p.eyebrow}</span>}
            {p.title && <h2 className={`mt-4 mb-6 ${dark ? 'text-white' : 'text-neutral-900'}`}>{p.title}</h2>}
            <RichText html={p.body} className={`mb-8 ${dark ? 'text-neutral-300' : 'text-neutral-600'}`} />
            {p.bullets?.length > 0 && <ul className="space-y-4">{p.bullets.map((b, i) => <li key={i} className="flex items-center gap-3"><Check className="w-5 h-5 text-amber-600 flex-shrink-0" /><span className={dark ? 'text-neutral-200' : 'text-neutral-600'}>{b.text}</span></li>)}</ul>}
            {p.show_contact_info && <div className="mt-8"><ContactDetails dark={dark} /></div>}
            {(p.aside_title || p.aside_body) && (
              <div className="mt-10 p-6 bg-amber-50 border border-amber-200 rounded-lg text-neutral-900" data-testid="cms-form-aside">
                {p.aside_title && <h4 className="!font-semibold !text-lg mb-2">{p.aside_title}</h4>}
                {p.aside_body && <p className="text-neutral-600 text-sm mb-4">{p.aside_body}</p>}
                {p.aside_button?.text && <SmartLink to={p.aside_button.link || '#'} className="inline-flex items-center justify-center h-9 px-4 py-2 text-sm font-medium border border-amber-600 text-amber-700 hover:bg-amber-100 rounded-md shadow-sm transition-colors" data-testid="cms-form-aside-btn">{p.aside_button.text}</SmartLink>}
              </div>
            )}
          </div>
          <div className="lg:col-span-3 bg-white p-8 lg:p-10 border border-neutral-200 text-neutral-900">{labeledForm}</div>
        </div>
      </SectionShell>
    );
  }

  if (layout === 'split') {
    // Mirrors the coded Contact page: 1/3 contact details, 2/3 labeled form.
    return (
      <SectionShell section={section}>
        <div className="grid lg:grid-cols-3 gap-12 lg:gap-20">
          <div>
            {p.title && <h3 className={`!text-xl mb-8 ${dark ? 'text-white' : ''}`}>{p.title}</h3>}
            <RichText html={p.body} className={`mb-8 ${dark ? 'text-neutral-300' : 'text-neutral-600'}`} />
            {p.bullets?.length > 0 && <ul className="space-y-3 mb-8">{p.bullets.map((b, i) => <li key={i} className={`flex items-center gap-3 ${dark ? 'text-neutral-200' : 'text-neutral-700'}`}><CheckCircle2 className="w-5 h-5 text-[color:var(--site-accent)] flex-shrink-0" />{b.text}</li>)}</ul>}
            {p.show_contact_info && <ContactDetails dark={dark} />}
          </div>
          <div className={`lg:col-span-2 ${dark ? 'text-neutral-900' : ''}`}>{labeledForm}</div>
        </div>
      </SectionShell>
    );
  }

  return (
    <SectionShell section={section} container="lg">
      <div className="max-w-2xl mx-auto"><SectionHeader props={p} style={{ align: 'center' }} dark={dark} />{form}</div>
    </SectionShell>
  );
};
